import type { Geometry } from '../../../../map-editor-entities/schema/types'
import type { Position } from '../../../../map-editor-entities/schema/exportSchema'

/**
 * Extract position from geometry as [x, y] tuple (compatible with router API)
 */
export function extractPosition(geometry: Geometry): Position {
  if (geometry.type === 'point') {
    return [geometry.coordinates[0], geometry.coordinates[1]]
  }

  // For non-point geometries, calculate centroid
  if (geometry.type === 'polygon' || geometry.type === 'polyline') {
    const coords = geometry.coordinates as any
    return calculateCentroid(coords)
  }

  throw new Error(`Cannot extract position from geometry type: ${(geometry as any).type}`)
}

/**
 * Extract points from geometry (for polygon/polyline objects)
 */
export function extractPoints(geometry: Geometry): Array<[number, number]> {
  if (geometry.type === 'polygon' || geometry.type === 'polyline') {
    return geometry.coordinates as any
  }

  throw new Error(`Cannot extract points from geometry type: ${(geometry as any).type}`)
}

/**
 * Expand insufficient points to a rectangle (min 3 points).
 * - 2 points → thin rectangle with offset perpendicular to the line
 * - 1 point → small square centered on the point
 * - 0 points → default small square at origin
 */
export function expandToRectangle(points: Array<[number, number]>): Array<[number, number]> {
  if (points.length >= 3) return points
  if (points.length === 2) {
    const [p1, p2] = points
    const dx = p2[0] - p1[0]
    const dy = p2[1] - p1[1]
    const len = Math.sqrt(dx * dx + dy * dy)
    const nx = len > 0 ? -dy / len : 0
    const ny = len > 0 ? dx / len : 1
    const offset = 2
    return [
      [p1[0] + nx * offset, p1[1] + ny * offset],
      [p2[0] + nx * offset, p2[1] + ny * offset],
      [p2[0] - nx * offset, p2[1] - ny * offset],
      [p1[0] - nx * offset, p1[1] - ny * offset]
    ]
  }
  if (points.length === 1) {
    const [x, y] = points[0]
    const s = 5
    return [[x - s, y - s], [x + s, y - s], [x + s, y + s], [x - s, y + s]]
  }
  return [[0, 0], [10, 0], [10, 10], [0, 10]]
}

/**
 * Calculate centroid of a polygon/polyline
 */
export function calculateCentroid(points: Array<[number, number]>): [number, number] {
  const sum = points.reduce(
    (acc, [x, y]) => ({ x: acc.x + x, y: acc.y + y }),
    { x: 0, y: 0 }
  )
  return [sum.x / points.length, sum.y / points.length]
}

/**
 * Calculate bounding box dimensions from points (vertices/polyline/single point).
 * Returns width/height in the SAME coordinate unit as the points themselves.
 *
 * This is the single source of truth for width/height in exports — replacing the
 * legacy properties.__size which mixed pixel-unit (icons) and transformed-unit
 * (polygons) values for different object types.
 *
 * Single point → (0, 0).
 */
export function calculateBounds(points: Array<[number, number]>): { width: number; height: number } {
  if (points.length === 0) return { width: 0, height: 0 }
  const xs = points.map(p => p[0])
  const ys = points.map(p => p[1])
  return {
    width: Math.max(...xs) - Math.min(...xs),
    height: Math.max(...ys) - Math.min(...ys)
  }
}
